import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ChevronLeftIcon, UploadIcon, BinIcon } from '@/components/icons';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { useCreateGallery, useGallery, useUpdateGallery } from '@/lib/api/hooks/gallery';
import { showToast } from '@/lib/toast';
import { Button, Loader, SaveDraftModal, PublishModal, Toggle } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import Input from '@/components/ui/Input/Input';
import TextArea from '@/components/ui/TextArea/TextArea';
import { cn } from '@/utils/helpers/classnames';
import { FolderIcon } from 'lucide-react';

const PhotoUploaderSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  photoUrl: z.array(z.string().url()).min(1, 'Upload at least one photo'),
  caption: z.string().trim().optional().or(z.literal('')),
  altText: z.string().trim().optional().or(z.literal('')),
  description: z.string().trim().optional().or(z.literal('')),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
});

type PhotoUploaderFormValues = z.infer<typeof PhotoUploaderSchema>;

type UploadedPhoto = {
  id: string;
  file?: File;
  preview: string;
  url?: string;
  uploading: boolean;
  uploadProgress: number;
  failed?: boolean;
  isExisting?: boolean;
};

const PhotoUploader: React.FC = () => {
  const { folderId, galleryId } = useParams<{ folderId: string; galleryId?: string }>();
  const navigate = useNavigate();
  const resolvedFolderId = folderId ?? '';
  const isEditMode = Boolean(galleryId);

  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhoto[]>([]);
  const [activeSaveAction, setActiveSaveAction] = useState<'DRAFT' | 'PUBLISH' | null>(null);
  const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
  const [showSaveDraftConfirmModal, setShowSaveDraftConfirmModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const { mutateAsync: uploadFile } = useFileUpload();
  const { mutateAsync: createGallery, isPending: isCreating } = useCreateGallery();
  const { mutateAsync: updateGallery, isPending: isUpdating } = useUpdateGallery();
  const { data: galleryData, isLoading: isLoadingGallery } = useGallery(galleryId || '');

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<PhotoUploaderFormValues>({
    resolver: zodResolver(PhotoUploaderSchema),
    defaultValues: {
      title: '',
      photoUrl: [],
      caption: '',
      altText: '',
      description: '',
      isKeyHighlight: false,
      isMultimedia: false,
    },
    mode: 'onChange',
  });

  const uploadedUrls = watch('photoUrl');

  useEffect(() => {
    if (!isEditMode || !galleryData?.data) return;

    const gallery = galleryData.data;

    const photoUrl = gallery.photoUrl;

    reset({
      title: gallery.title || '',
      photoUrl: gallery.photoUrl ? [gallery.photoUrl] : [],
      caption: gallery.caption || '',
      altText: gallery.altText || '',
      description: gallery.description || '',
      isKeyHighlight: Boolean(gallery.isKeyHighlight),
      isMultimedia: Boolean(gallery.isMultimedia),
    });

    setUploadedPhotos(
      photoUrl
        ? [
            {
              id: `existing-${gallery.galleryId}`,
              preview: photoUrl,
              url: photoUrl,
              uploading: false,
              uploadProgress: 100,
              failed: false,
              isExisting: true,
            },
          ]
        : []
    );
  }, [isEditMode, galleryData, reset]);

  useEffect(() => {
    return () => {
      uploadedPhotos.forEach((photo) => {
        if (photo.preview.startsWith('blob:')) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, [uploadedPhotos]);

  const upsertUploadedPhoto = useCallback((photoId: string, partial: Partial<UploadedPhoto>) => {
    setUploadedPhotos((prev) => prev.map((photo) => (photo.id === photoId ? { ...photo, ...partial } : photo)));
  }, []);

  const uploadSingleFile = useCallback(
    async (photo: UploadedPhoto) => {
      if (!photo.file) return;

      try {
        const result = await uploadFile({ file: photo.file, category: 'IMAGE' });
        if (!result?.success || !result.data?.fileUrl) {
          throw new Error('Upload failed');
        }

        upsertUploadedPhoto(photo.id, {
          url: result.data.fileUrl,
          uploading: false,
          uploadProgress: 100,
          failed: false,
        });

        const currentUrls = getValues('photoUrl');
        setValue('photoUrl', isEditMode ? [result.data.fileUrl] : [...currentUrls, result.data.fileUrl], {
          shouldDirty: true,
          shouldValidate: true,
        });
      } catch (error) {
        console.error('Upload error:', error);
        upsertUploadedPhoto(photo.id, {
          uploading: false,
          failed: true,
          uploadProgress: 0,
        });
        showToast.error(`Failed to upload ${photo.file.name}`);
      }
    },
    [uploadFile, upsertUploadedPhoto, getValues, setValue, isEditMode]
  );

  const handleFileSelect = useCallback(
    async (files: File[] | null) => {
      if (!files || files.length === 0) return;

      const selectedFiles = isEditMode ? [files[0]] : files;

      if (isEditMode) {
        setValue('photoUrl', [], { shouldDirty: true, shouldValidate: true });
      }

      const nextPhotos: UploadedPhoto[] = selectedFiles.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
        preview: URL.createObjectURL(file),
        uploading: true,
        uploadProgress: 35,
      }));

      setUploadedPhotos((prev) => {
        if (!isEditMode) return [...prev, ...nextPhotos];

        prev.forEach((photo) => {
          if (photo.preview.startsWith('blob:')) {
            URL.revokeObjectURL(photo.preview);
          }
        });
        return nextPhotos;
      });

      await Promise.all(nextPhotos.map((photo) => uploadSingleFile(photo)));
    },
    [uploadSingleFile, isEditMode, setValue]
  );

  const removeFile = useCallback(
    (photoId: string) => {
      setUploadedPhotos((prev) => {
        const target = prev.find((photo) => photo.id === photoId);
        if (target?.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(target.preview);
        }

        if (target?.url) {
          setValue(
            'photoUrl',
            getValues('photoUrl').filter((url) => url !== target.url),
            { shouldDirty: true, shouldValidate: true }
          );
        }

        return prev.filter((photo) => photo.id !== photoId);
      });
    },
    [getValues, setValue]
  );

  const submitWithStatus = useCallback(
    async (values: PhotoUploaderFormValues, status: 'DRAFT' | 'PUBLISH') => {
      setActiveSaveAction(status);
      try {
        const response = isEditMode && galleryId
          ? await updateGallery({
              galleryId,
              data: {
                folderId: resolvedFolderId,
                title: values.title,
                caption: values.caption,
                altText: values.altText,
                description: values.description,
                photoUrl: values.photoUrl[0],
                isKeyHighlight: values.isKeyHighlight,
                isMultimedia: values.isMultimedia,
                status,
              },
            })
          : await createGallery({
              ...values,
              folderId: resolvedFolderId,
              status,
            });

        if (!response.success) {
          throw new Error(response.message || 'Failed to save gallery');
        }

        showToast.success(
          `Gallery ${isEditMode ? 'updated and ' : ''}${status === 'PUBLISH' ? 'published' : 'saved as draft'} successfully!`
        );
        navigate(`/admin/photo-gallery/${resolvedFolderId}`);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save gallery';
        showToast.error(message);
      } finally {
        setActiveSaveAction(null);
      }
    },
    [isEditMode, galleryId, updateGallery, createGallery, resolvedFolderId, navigate]
  );

  const saveDraft = handleSubmit((values) => submitWithStatus(values, 'DRAFT'));
  const publishGallery = handleSubmit((values) => submitWithStatus(values, 'PUBLISH'));

  const handleGoBack = () => {
    if (isDirty || uploadedPhotos.length > 0) {
      setShowSaveDraftModal(true);
    } else {
      navigate(`/admin/photo-gallery/${resolvedFolderId}`);
    }
  };

  const handleSaveDraftFromModal = () => {
    setShowSaveDraftModal(false);
    saveDraft();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + 'b';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + 'kb';
    return (bytes / (1024 * 1024)).toFixed(1) + 'mb';
  };

  const isAnyUploading = useMemo(
    () => uploadedPhotos.some((photo) => photo.uploading),
    [uploadedPhotos]
  );

  const isBusy = isCreating || isUpdating || isAnyUploading;

  if (!folderId) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Folder ID missing</h2>
        <button onClick={() => navigate('/admin/photo-gallery')} className="mt-4 text-[#047857] hover:underline">
          &larr; Back to Photo Gallery
        </button>
      </div>
    );
  }

  if (isEditMode && isLoadingGallery) {
    return (
      <div className="bg-white rounded-xl p-10">
        <Loader size="md" variant="wine" message="Loading gallery..." />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl">
      {/* Header */}
      <header className="p-4 flex justify-between z-10 sticky top-0 bg-white items-center border-b border-gray-200">
        <div className="flex items-center gap-4">
          <Button
            onClick={handleGoBack}
            variant="ghost"
            className="flex items-center gap-2 !px-0 !py-0 text-sm font-semibold text-gray-700 hover:text-gray-900"
            disabled={isBusy}
          >
            <ChevronLeftIcon className="w-5 h-5" />
            Go back
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={() => setShowSaveDraftConfirmModal(true)}
            variant="secondary"
            disabled={isBusy}
            loading={activeSaveAction === 'DRAFT'}
          >
            Save as Draft
          </Button>
          <Button
            onClick={() => setShowPublishModal(true)}
            disabled={!isValid || isBusy || uploadedUrls.length === 0}
            loading={activeSaveAction === 'PUBLISH'}
          >
            Publish
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6 md:p-10 max-w-6xl mx-auto space-y-6">
        {/* Upload Area */}
        <Controller
          name="photoUrl"
          control={control}
          render={() => (
            <FileUpload
              showPreview={false}
              multiple={!isEditMode}
              onChange={handleFileSelect}
              allowedFileTypes={['image/*']}
              $maxSizeMB={10}
              error={errors.photoUrl?.message}
              containerClassName={cn(
                'rounded-xl border border-dashed border-[#9BC3AB] bg-[#FAFCFB] p-6 md:p-8'
              )}
              placeholder=""
            >
              <div className="pointer-events-none grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr] md:items-center">
                <div className="hidden md:flex justify-center">
                  <div className="h-28 w-44 rounded-xl bg-[#EAF3EE] flex items-center justify-center">
                    <UploadIcon className="h-12 w-12 text-[#0D6B3C]" />
                  </div>
                </div>
                <div className="text-center flex flex-col items-center">
                  <p className="font-semibold">
                    {isEditMode ? 'Drag and drop a file here to replace photo' : 'Drag and drop files here to upload'}
                  </p>
                  <div className="my-4 flex items-center gap-3 text-sm text-gray-400">
                    <span className="h-px w-24 bg-gray-200" />
                    or
                    <span className="h-px w-24 bg-gray-200" />
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <Button type="button" className="pointer-events-none" icon={<FolderIcon className="h-4 w-4" />}>
                    
                      Browse Files
                    </Button>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Supported formats: PNG, JPG, JPEG, WEBP, GIF
                  </p>
                  <p className="text-sm text-gray-500">
                    Max file size: 10MB
                  </p>
                </div>
              </div>
            </FileUpload>
          )}
        />

        {/* Uploaded Photos List */}
        {uploadedPhotos.length > 0 && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              {isEditMode ? 'Uploaded Photo' : `Uploaded Photos (${uploadedPhotos.length})`}
            </label>
            {uploadedPhotos.map((photo) => (
              <div
                key={photo.id}
                className="flex items-center justify-between gap-4 p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {/* Thumbnail */}
                  <img
                    src={photo.preview}
                    alt={photo.file?.name || 'Uploaded photo'}
                    className="w-12 h-12 object-cover rounded-lg"
                  />
                  <div>
                    <p className="font-semibold text-gray-900 truncate max-w-[260px]">
                      {photo.file?.name || 'Existing photo'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {photo.file ? formatFileSize(photo.file.size) : 'Already uploaded'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-1 items-center gap-3">
                  {/* Upload Progress or Status */}
                  {photo.uploading ? (
                    <div className="flex w-full items-center gap-3">
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#047857] transition-all duration-300"
                          style={{ width: `${photo.uploadProgress}%` }}
                        />
                      </div>
                      <Loader size="xs" variant="wine" />
                      <span className="text-xs text-gray-500">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex w-full items-center gap-3">
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={cn('h-full transition-all duration-300', photo.failed ? 'bg-red-500' : 'bg-[#047857]')}
                          style={{ width: photo.failed ? '35%' : '100%' }}
                        />
                      </div>
                      <span className={cn('text-xs font-medium', photo.failed ? 'text-red-600' : 'text-gray-600')}>
                        {photo.failed ? 'Failed' : '100%'}
                      </span>
                    </div>
                  )}

                  {/* Delete Button */}
                  <Button
                    onClick={() => removeFile(photo.id)}
                    disabled={photo.uploading || isCreating || isUpdating}
                    variant="secondary"
                    size="sm"
                    className="!text-[#A8393D] !border-[#E6BFC1] hover:!bg-[#FFF5F6]"
                    aria-label="Remove file"
                  >
                    {photo.uploading ? 'Cancel' : (
                      <>
                        <BinIcon className="w-4 h-4 text-[#A8393D]" /> Remove
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Metadata Form */}
        <div className="grid grid-cols-1 gap-5 gap-y-6 md:grid-cols-2">
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input
                label="Title"
                required
                placeholder="Title"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.title?.message}
                $marginbottom="0"
              />
            )}
          />

          <Controller
            name="caption"
            control={control}
            render={({ field }) => (
              <Input
                label="Caption"
                placeholder="Caption"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                $marginbottom="0"
              />
            )}
          />

          <Controller
            name="altText"
            control={control}
            render={({ field }) => (
              <Input
                label="Alt Text"
                placeholder="Alt Text"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                $marginbottom="0"
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                label="Description"
                placeholder="Description"
                value={field.value ?? ''}
                onChange={field.onChange}
                maxLength={500}
                error={errors.description?.message as string | undefined}
                containerClassName="mb-0"
                textAreaClassName="bg-[var(--inputBgColor)] min-h-[7.5rem] text-text-primary"
              />
            )}
          />

          <div className="md:col-span-2 rounded-xl border border-gray-200 bg-[#FCFDFC] p-4 space-y-4">
            <Controller
              name="isKeyHighlight"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={field.value}
                  onChange={field.onChange}
                  label="Mark as Key Highlight"
                  description="Marks the gallery as a key highlight."
                  disabled={isBusy}
                />
              )}
            />

            <Controller
              name="isMultimedia"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={field.value}
                  onChange={field.onChange}
                  label="Is Multimedia"
                  description="Marks the gallery as multimedia content."
                  disabled={isBusy}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Save to Draft Modal (for unsaved changes when going back) */}
      <SaveDraftModal
        isOpen={showSaveDraftModal}
        onClose={() => setShowSaveDraftModal(false)}
        onConfirm={handleSaveDraftFromModal}
        onDiscard={() => navigate(`/admin/photo-gallery/${resolvedFolderId}`)}
        title="Save Gallery?"
        subtitle="You can save this and publish later"
        isLoading={activeSaveAction === 'DRAFT'}
      />

      {/* Save Draft Confirmation Modal */}
      <SaveDraftModal
        isOpen={showSaveDraftConfirmModal}
        onClose={() => setShowSaveDraftConfirmModal(false)}
        onConfirm={() => {
          setShowSaveDraftConfirmModal(false);
          saveDraft();
        }}
        title="Save as Draft?"
        subtitle="Your gallery will be saved and can be published later"
        isLoading={activeSaveAction === 'DRAFT'}
      />

      {/* Publish Confirmation Modal */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={() => {
          setShowPublishModal(false);
          publishGallery();
        }}
        title="Publish Gallery?"
        subtitle="Are you sure you want to publish this gallery?"
        itemName={watch('title')}
        infoText="This gallery will be visible to the public once published."
        itemType="Gallery"
        isLoading={activeSaveAction === 'PUBLISH'}
      />
    </div>
  );
};

export default PhotoUploader;
